import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  Select,
  DatePicker,
  Button,
  Card,
  Row,
  Col,
  message,
  Spin,
  Typography,
  Switch,
  Space,
  Modal,
  Divider,
  Tag,
  InputNumber,
} from "antd";
import {
  SaveOutlined,
  ArrowLeftOutlined,
  UserOutlined,
  EyeOutlined,
  EditOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  PlusOutlined,
  DeleteOutlined,
  LockOutlined,
  SettingOutlined,
  QuestionCircleOutlined,
  BookOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import {
  createDossier,
  updateDossier,
  fetchDossierById,
  clearCurrentDossier,
} from "../../../store/slices/dossierSlice";
import {
  fetchRegions,
  fetchDepartements,
  fetchGroupeClients,
  fetchTypologies,
  fetchUsers,
  fetchProvincesByRegion,
  fetchCommunesByProvince,
} from "../../../store/slices/referenceSlice";
import { RootState, AppDispatch } from "../../../store";
import { Dossier, WorkflowAssignment } from "../../../types";
import { dossierApi } from "../../../api/dossiers";
import {
  FormContainer,
  FormCard,
  FormActions,
  WorkflowCard,
} from "./DossierForm.styles";
import WorkflowAssignments from "../WorkflowAssignments/WorkflowAssignments";
import { formatDateTime } from "../../../utils/dateUtils";

const { Option } = Select;
const { Text, Title } = Typography;

interface DossierFormProps {
  mode: "create" | "edit";
}

interface TempWorkflowAssignment {
  user: number;
  is_primary: boolean;
  date_started?: string;
  date_completed?: string;
  date_prevalidation?: string; // Only for validators
}

interface WorkflowFormData {
  visiteurs: TempWorkflowAssignment[];
  elaborateurs: TempWorkflowAssignment[];
  validateurs: TempWorkflowAssignment[];
}

const DossierForm: React.FC<DossierFormProps> = ({ mode }) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const { currentDossier, loading } = useSelector(
    (state: RootState) => state.dossiers
  );
  const {
    regions,
    provinces,
    communes,
    departements,
    groupeClients,
    typologies,
    users,
    loading: referencesLoading,
  } = useSelector((state: RootState) => state.references);
  const { user } = useSelector((state: RootState) => state.auth);

  const [selectedRegion, setSelectedRegion] = useState<number | null>(null);
  const [selectedProvince, setSelectedProvince] = useState<number | null>(null);
  const [filteredProvinces, setFilteredProvinces] = useState<any[]>([]);
  const [filteredCommunes, setFilteredCommunes] = useState<any[]>([]);
  const [saving, setSaving] = useState(false); // New state for save operation
  const [dataLoaded, setDataLoaded] = useState(false); // New state to track if data is loaded

  // Workflow assignments state
  const [visiteurs, setVisiteurs] = useState<WorkflowAssignment[]>([]);
  const [elaborateurs, setElaborateurs] = useState<WorkflowAssignment[]>([]);
  const [validateurs, setValidateurs] = useState<WorkflowAssignment[]>([]);

  // For create mode - temporary workflow data
  const [tempWorkflowData, setTempWorkflowData] = useState<WorkflowFormData>({
    visiteurs: [],
    elaborateurs: [],
    validateurs: [],
  });

  useEffect(() => {
    // Clear previous data and set loading state
    if (mode === "edit") {
      setDataLoaded(false);
    } else {
      setDataLoaded(true); // For create mode, no need to wait for data
    }

    // Load reference data
    dispatch(fetchRegions());
    dispatch(fetchDepartements());
    dispatch(fetchGroupeClients());
    dispatch(fetchTypologies());
    dispatch(fetchUsers());

    if (mode === "edit" && id) {
      dispatch(fetchDossierById(Number(id)));
    }

    return () => {
      dispatch(clearCurrentDossier());
      setDataLoaded(false);
    };
  }, [dispatch, mode, id]);

  useEffect(() => {
    if (mode === "edit" && currentDossier) {
      // Populate form with existing data
      const formData = {
        ...currentDossier,
        Date_reception_demande: currentDossier.Date_reception_demande
          ? dayjs(currentDossier.Date_reception_demande)
          : null,
        Date_envoi_creation: currentDossier.Date_envoi_creation
          ? dayjs(currentDossier.Date_envoi_creation)
          : null,
        date_livraison_convenue: currentDossier.date_livraison_convenue
          ? dayjs(currentDossier.date_livraison_convenue)
          : null,
        date_envoi_rapport: currentDossier.date_envoi_rapport
          ? dayjs(currentDossier.date_envoi_rapport)
          : null,
        date_modification: currentDossier.date_modification
          ? dayjs(currentDossier.date_modification)
          : null,
      };

      form.setFieldsValue(formData);

      // Set workflow assignments
      setVisiteurs(currentDossier.visiteurs || []);
      setElaborateurs(currentDossier.elaborateurs || []);
      setValidateurs(currentDossier.validateurs || []);

      // Set selected region and province for cascading selects
      if (currentDossier.fk_region) {
        setSelectedRegion(currentDossier.fk_region);
        dispatch(fetchProvincesByRegion(currentDossier.fk_region));
      }

      if (currentDossier.fk_province) {
        setSelectedProvince(currentDossier.fk_province);
        dispatch(fetchCommunesByProvince(currentDossier.fk_province));
      }

      // Mark data as loaded
      setDataLoaded(true);
    }
  }, [currentDossier, form, mode, dispatch]);

  const handleRegionChange = (regionId: number) => {
    setSelectedRegion(regionId);
    setSelectedProvince(null);
    form.setFieldsValue({ fk_province: null, fk_commune: null });
    dispatch(fetchProvincesByRegion(regionId));
    setFilteredCommunes([]);
  };

  const handleProvinceChange = (provinceId: number) => {
    setSelectedProvince(provinceId);
    form.setFieldsValue({ fk_commune: null });
    dispatch(fetchCommunesByProvince(provinceId));
  };

  useEffect(() => {
    setFilteredProvinces(
      provinces.filter((p) => p.fk_region === selectedRegion)
    );
  }, [provinces, selectedRegion]);

  useEffect(() => {
    setFilteredCommunes(
      communes.filter((c) => c.fk_province === selectedProvince)
    );
  }, [communes, selectedProvince]);

  // Workflow assignment handlers for EDIT mode
  const handleAddAssignment = async (
    assignment: Partial<WorkflowAssignment>
  ) => {
    if (!currentDossier) return;

    try {
      const newAssignment = await dossierApi.addWorkflowAssignment(
        currentDossier.id,
        assignment
      );

      // Update local state
      switch (assignment.role) {
        case "visiteur":
          setVisiteurs([...visiteurs, newAssignment]);
          break;
        case "elaborateur":
          setElaborateurs([...elaborateurs, newAssignment]);
          break;
        case "validateur":
          setValidateurs([...validateurs, newAssignment]);
          break;
      }

      message.success(`${assignment.role} assigné avec succès`);

      // Refresh dossier to update state
      dispatch(fetchDossierById(currentDossier.id));
    } catch (error) {
      message.error("Erreur lors de l'assignation");
    }
  };

  const canGestionnaireChangeState = (
    validateurs: WorkflowAssignment[],
    currentState: string
  ): boolean => {
    console.log("🔍 Checking state control permissions:");
    console.log("Validateurs:", validateurs);
    console.log("Current state:", currentState);

    // Rule 1: At least one validator must be assigned
    if (!validateurs || validateurs.length === 0) {
      console.log("❌ No validators assigned");
      return false;
    }

    // Rule 2: At least one validator must have started their work
    const validatorsWithStartDate = validateurs.filter((v) => v.date_started);
    console.log("Validators with start date:", validatorsWithStartDate);

    if (validatorsWithStartDate.length === 0) {
      console.log("❌ No validators have started");
      return false;
    }

    console.log("✅ Manual control allowed");
    return true;
  };

  const getEtatColor = (etat: string) => {
    const colors = {
      en_attente_visite: "blue",
      en_cours_visite: "processing",
      en_attente_elaboration: "orange",
      en_cours_elaboration: "processing",
      en_attente_validation: "gold",
      en_cours_validation: "processing",
      en_attente_modification: "red",
      en_cours_modification: "processing",
      modifie_att_validation: "purple", // NOUVEAU
      valide_att_honoraires: "purple",
      valide_a_envoyer: "green",
      envoye: "success",
    };
    return colors[etat as keyof typeof colors] || "default";
  };

  const getEtatText = (etat: string) => {
    const texts = {
      en_attente_visite: "En attente de visite",
      en_cours_visite: "En cours de visite",
      en_attente_elaboration: "En attente d'élaboration",
      en_cours_elaboration: "En cours d'élaboration",
      en_attente_validation: "En attente de validation",
      en_cours_validation: "En cours de validation",
      en_attente_modification: "En attente de modification",
      en_cours_modification: "En cours de modification",
      modifie_att_validation: "Modifié - Att. validation", // NOUVEAU
      valide_att_honoraires: "Validé - Att. honoraires",
      valide_a_envoyer: "Validé - À envoyer",
      envoye: "Envoyé",
    };
    return texts[etat as keyof typeof texts] || etat;
  };

  const handleUpdateAssignment = async (
    assignmentId: number,
    data: Partial<WorkflowAssignment>
  ) => {
    if (!currentDossier) return;

    try {
      await dossierApi.updateWorkflowAssignment(
        currentDossier.id,
        assignmentId,
        data
      );

      // Update local state
      const updateAssignments = (assignments: WorkflowAssignment[]) =>
        assignments.map((a) => (a.id === assignmentId ? { ...a, ...data } : a));

      setVisiteurs(updateAssignments(visiteurs));
      setElaborateurs(updateAssignments(elaborateurs));
      setValidateurs(updateAssignments(validateurs));

      // Refresh dossier to update state
      dispatch(fetchDossierById(currentDossier.id));
    } catch (error) {
      message.error("Erreur lors de la mise à jour");
    }
  };

  const handleRemoveAssignment = async (assignmentId: number) => {
    if (!currentDossier) return;

    try {
      await dossierApi.removeWorkflowAssignment(
        currentDossier.id,
        assignmentId
      );

      // Update local state
      setVisiteurs(visiteurs.filter((a) => a.id !== assignmentId));
      setElaborateurs(elaborateurs.filter((a) => a.id !== assignmentId));
      setValidateurs(validateurs.filter((a) => a.id !== assignmentId));

      message.success("Assignation supprimée avec succès");

      // Refresh dossier to update state
      dispatch(fetchDossierById(currentDossier.id));
    } catch (error) {
      message.error("Erreur lors de la suppression");
    }
  };

  // Handlers for CREATE mode - temporary workflow data
  const handleTempAddAssignment = (
    role: "visiteur" | "elaborateur" | "validateur",
    userId: number,
    isPrimary: boolean = false
  ) => {
    const roleKey = `${role}s` as keyof WorkflowFormData;
    const currentAssignments = tempWorkflowData[roleKey];

    // Check if user already assigned
    if (currentAssignments.some((a) => a.user === userId)) {
      message.warning("Cet utilisateur est déjà assigné à ce rôle");
      return;
    }

    setTempWorkflowData((prev) => ({
      ...prev,
      [roleKey]: [
        ...currentAssignments,
        { user: userId, is_primary: isPrimary },
      ],
    }));
  };

  const handleTempRemoveAssignment = (
    role: "visiteur" | "elaborateur" | "validateur",
    userId: number
  ) => {
    const roleKey = `${role}s` as keyof WorkflowFormData;
    const currentAssignments = tempWorkflowData[roleKey];

    setTempWorkflowData((prev) => ({
      ...prev,
      [roleKey]: currentAssignments.filter((a) => a.user !== userId),
    }));
  };

  const handleTempUpdatePrimary = (
    role: "visiteur" | "elaborateur" | "validateur",
    userId: number,
    isPrimary: boolean
  ) => {
    const roleKey = `${role}s` as keyof WorkflowFormData;
    const currentAssignments = tempWorkflowData[roleKey];

    setTempWorkflowData((prev) => ({
      ...prev,
      [roleKey]: currentAssignments.map((a) =>
        a.user === userId ? { ...a, is_primary: isPrimary } : a
      ),
    }));
  };

  const formatNumber = (value: string | number | undefined): string => {
    if (!value) return "";
    return `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  };

  const parseNumber = (value: string | undefined): number => {
    if (!value) return 0;
    return parseFloat(value.replace(/\s/g, "")) || 0;
  };

  const onFinish = async (values: any) => {
    setSaving(true); // Start loading

    try {
      const formattedValues = {
        ...values,
        Date_reception_demande: values.Date_reception_demande
          ? values.Date_reception_demande.format("YYYY-MM-DD")
          : null,
        Date_envoi_creation: values.Date_envoi_creation
          ? values.Date_envoi_creation.format("YYYY-MM-DD")
          : null,
        date_livraison_convenue: values.date_livraison_convenue
          ? values.date_livraison_convenue.format("YYYY-MM-DD")
          : null,
        date_envoi_rapport: values.date_envoi_rapport
          ? values.date_envoi_rapport.format("YYYY-MM-DD")
          : null,
        date_modification: values.date_modification
          ? values.date_modification.format("YYYY-MM-DD")
          : null,
        fk_user_gestionnaire: user?.id,
      };

      if (mode === "create") {
        // Create dossier first
        const newDossier = await dispatch(
          createDossier(formattedValues)
        ).unwrap();

        // Then create workflow assignments if any
        const allAssignments: Array<{
          role: "visiteur" | "elaborateur" | "validateur";
          user: number;
          is_primary: boolean;
          date_started?: string;
          date_completed?: string;
          date_prevalidation?: string;
        }> = [
          ...tempWorkflowData.visiteurs.map((v) => ({
            ...v,
            role: "visiteur" as const,
          })),
          ...tempWorkflowData.elaborateurs.map((e) => ({
            ...e,
            role: "elaborateur" as const,
          })),
          ...tempWorkflowData.validateurs.map((v) => ({
            ...v,
            role: "validateur" as const,
          })),
        ];

        for (const assignment of allAssignments) {
          try {
            await dossierApi.addWorkflowAssignment(newDossier.id, assignment);
          } catch (error) {
            console.error("Error adding workflow assignment:", error);
          }
        }

        message.success("Dossier créé avec succès");
        navigate("/dossiers"); // Navigate away only for create mode
      } else if (mode === "edit" && id) {
        await dispatch(
          updateDossier({ id: Number(id), data: formattedValues })
        );

        // Refresh the current dossier data to show updates
        dispatch(fetchDossierById(Number(id)));

        message.success("Dossier modifié avec succès");
        // Don't navigate away - stay on the edit page
      }
    } catch (error) {
      message.error("Erreur lors de la sauvegarde");
    } finally {
      setSaving(false); // Stop loading
    }
  };

  const conservationOptions = [
    "AGADIR",
    "ELHAOUZ",
    "AL HOCEIMA",
    "AZEMMOUR",
    "AZILAL",
    "BEN GUERIR",
    "BENI MELLAL",
    "BENSLIMANE",
    "BERKANE",
    "BERRECHID",
    "BOULMANE",
    "MISSOUR",
    "CASA AIN CHOCK",
    "CASA AIN SBAA HAY MOHAMMADI",
    "CASA ANFA",
    "CASA ALFIDA MERS SULTAN",
    "CASA HAY HASSANI",
    "CASA MAARIF",
    "CASA NOUACEUR",
    "CASA SIDI EL BERNOUSSI - ZENATA",
    "CASA SIDI OTHMANE",
    "CHICHAOUA",
    "CHTOUKA AIT BAHA",
    "EL HAJEB",
    "EL JADIDA",
    "EL KELAA DES SRAGHNA",
    "ERRACHIDIA",
    "ESSAOUIRA",
    "FES",
    "FES ZOUAGHA",
    "FQUIH BEN SALEH",
    "GUELMIM",
    "GUERCIF",
    "HAD SOUALEM",
    "HARHOURA-SKHIRAT",
    "IFRANE",
    "INZEGANE AIT MELLOUL",
    "KARIAT BA MOHAMED",
    "KENITRA",
    "KHMISSET",
    "KHNIFRA",
    "KHOURIBGA",
    "LAAYOUNE",
    "LARACHE",
    "MARRAKECH GUELIZ",
    "MARRAKECH MENARA",
    "MARRAKECH SIDI YOUSSEF BEN ALI",
    "M'DIQ FNIDEQ",
    "MEDYOUNA",
    "MEKNES AL ISMAILIA",
    "MEKNES EL MENZEH",
    "MIDELT",
    "MOHAMMADIA",
    "NADOR",
    "OUARZAZATE",
    "OUJDA",
    "OUJDA ANGAD",
    "RABAT",
    "RABAT RYAD",
    "ROMMANI",
    "SAFI",
    "SALE",
    "SALE EL JADIDA",
    "SEFROU",
    "SETTAT",
    "SIDI BENNOUR",
    "SIDI KACEM",
    "SIDI SLIMANE",
    "SIDI SMAIL",
    "ZEMAMRA",
    "SOUK LARBAA",
    "SOUK SEBT OULED NEMMA",
    "TAMESNA",
    "TANGER BENI MAKADA",
    "TANGER VILLE",
    "TAOUNAT",
    "TAOURIRT",
    "TAROUDANT",
    "TAZA",
    "TEMARA",
    "TETOUAN",
    "TIFELT",
    "TIZNIT",
  ];

  const getUsersByRole = (role: string) => {
    return users.filter((u) => u.role === role && u.is_active);
  };

  const canAssignValidators = () => {
    return true;
  };

  // Add this helper function at the top of the component

  // Simple workflow assignment component for CREATE mode
  const SimpleWorkflowSection = ({
    role,
    title,
    icon,
  }: {
    role: "visiteur" | "elaborateur" | "validateur";
    title: string;
    icon: React.ReactNode;
  }) => {
    const roleKey = `${role}s` as keyof WorkflowFormData;
    const assignments = tempWorkflowData[roleKey];
    const availableUsers = getUsersByRole(role).filter(
      (u) => !assignments.some((a) => a.user === u.id)
    );

    const handleUserSelect = (userId: number | undefined) => {
      if (userId) {
        handleTempAddAssignment(role, userId);
      }
    };

    const handleDateChange = (
      assignmentIndex: number,
      field: string,
      date: any
    ) => {
      const currentAssignments = tempWorkflowData[roleKey];
      const updatedAssignments = currentAssignments.map((assignment, index) =>
        index === assignmentIndex
          ? {
              ...assignment,
              [field]: date
                ? typeof date === "string"
                  ? date
                  : date.format("YYYY-MM-DD")
                : null,
            }
          : assignment
      );

      setTempWorkflowData((prev) => ({
        ...prev,
        [roleKey]: updatedAssignments,
      }));
    };

    return (
      <Card
        size="small"
        title={
          <div style={{ display: "flex", alignItems: "center" }}>
            {icon}
            <span style={{ marginLeft: 8 }}>{title}</span>
          </div>
        }
      >
        {assignments.map((assignment, index) => {
          const assignedUser = users.find((u) => u.id === assignment.user);
          return (
            <div
              key={index}
              style={{
                marginBottom: 16,
                padding: 12,
                border: "1px solid #f0f0f0",
                borderRadius: 6,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: 8,
                }}
              >
                <Text>
                  {assignedUser?.first_name} {assignedUser?.last_name}
                </Text>
                <Switch
                  size="small"
                  checked={assignment.is_primary}
                  onChange={(checked) =>
                    handleTempUpdatePrimary(role, assignment.user, checked)
                  }
                  style={{ marginLeft: 8, marginRight: 8 }}
                />
                <Text
                  type="secondary"
                  style={{ fontSize: "12px", marginRight: 8 }}
                >
                  Principal
                </Text>
                <Button
                  type="text"
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={() =>
                    handleTempRemoveAssignment(role, assignment.user)
                  }
                />
              </div>

              {/* Date fields */}
              <Row gutter={8}>
                {role === "validateur" && (
                  <Col span={8}>
                    <Text type="secondary" style={{ fontSize: "12px" }}>
                      Prévalidation:
                    </Text>
                    <DatePicker
                      size="small"
                      value={
                        assignment.date_prevalidation
                          ? dayjs(assignment.date_prevalidation)
                          : null
                      }
                      onChange={(date) =>
                        handleDateChange(
                          index,
                          "date_prevalidation",
                          date ? date.format("YYYY-MM-DD") : null
                        )
                      }
                      format="DD/MM/YYYY"
                      style={{ width: "100%" }}
                      placeholder="Date prévalidation"
                    />
                  </Col>
                )}
                <Col span={role === "validateur" ? 8 : 12}>
                  <Text type="secondary" style={{ fontSize: "12px" }}>
                    Date début:
                  </Text>
                  <DatePicker
                    size="small"
                    value={
                      assignment.date_started
                        ? dayjs(assignment.date_started)
                        : null
                    }
                    onChange={(date) =>
                      handleDateChange(
                        index,
                        "date_started",
                        date ? date.format("YYYY-MM-DD") : null
                      )
                    }
                    format="DD/MM/YYYY"
                    style={{ width: "100%" }}
                    placeholder="Date début"
                  />
                </Col>
                <Col span={role === "validateur" ? 8 : 12}>
                  <Text type="secondary" style={{ fontSize: "12px" }}>
                    Date fin:
                  </Text>
                  <DatePicker
                    size="small"
                    value={
                      assignment.date_completed
                        ? dayjs(assignment.date_completed)
                        : null
                    }
                    onChange={(date) =>
                      handleDateChange(
                        index,
                        "date_completed",
                        date ? date.format("YYYY-MM-DD") : null
                      )
                    }
                    format="DD/MM/YYYY"
                    style={{ width: "100%" }}
                    placeholder="Date fin"
                  />
                </Col>
              </Row>
            </div>
          );
        })}

        {availableUsers.length > 0 && (
          <Select
            placeholder={`Ajouter un ${role}`}
            style={{ width: "100%", marginTop: 8 }}
            onSelect={handleUserSelect}
            value={undefined}
          >
            {availableUsers.map((user) => (
              <Option key={user.id} value={user.id}>
                {user.first_name} {user.last_name}
              </Option>
            ))}
          </Select>
        )}
      </Card>
    );
  };

  if (mode === "edit" && (!dataLoaded || loading)) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "calc(100vh - 120px)", // Account for header height
          position: "relative",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(255, 255, 255, 0.9)",
            zIndex: 1000,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Spin size="large" />
          <div style={{ marginTop: 16, color: "#666", fontSize: 16 }}>
            Chargement du dossier...
          </div>
        </div>
      </div>
    );
  }

  // Show loading spinner during save operations
  if (saving) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "calc(100vh - 120px)", // Account for header height
          position: "relative",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(255, 255, 255, 0.9)",
            zIndex: 1000,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Spin size="large" />
          <div style={{ marginTop: 16, color: "#666", fontSize: 16 }}>
            Sauvegarde en cours...
          </div>
        </div>
      </div>
    );
  }

  const canUseModificationStates = () => {
    if (mode === "create") return false;
    if (!currentDossier) return false;

    // Utiliser directement currentDossier.validateurs
    const validateurs = currentDossier.validateurs || [];

    // Vérifier qu'au moins un validateur est assigné ET a une date de début
    const hasValidatorStarted = validateurs.some(
      (v) => v.user && v.date_started
    );

    return hasValidatorStarted;
  };

  // Fonction pour changer l'état automatiquement
  const handleStateChangeAuto = async (newState: string) => {
    if (!currentDossier) {
      message.error("Erreur: Dossier non chargé");
      return;
    }

    // Vérifier que l'état sélectionné est autorisé
    const modificationStates = [
      "en_attente_modification",
      "en_cours_modification",
    ];

    if (modificationStates.includes(newState) && !canUseModificationStates()) {
      message.error(
        "Un validateur doit être assigné et avoir commencé son travail"
      );
      // Remettre l'ancienne valeur dans le formulaire
      form.setFieldsValue({ etat_dossier: currentDossier.etat_dossier });
      return;
    }

    // Si c'est l'état actuel, ne rien faire
    if (newState === currentDossier.etat_dossier) {
      return;
    }

    try {
      await dossierApi.setModificationState(currentDossier.id, newState);
      message.success(`État mis à jour vers: ${getEtatText(newState)}`);
      dispatch(fetchDossierById(currentDossier.id));
    } catch (error: any) {
      // En cas d'erreur, remettre l'ancienne valeur
      form.setFieldsValue({ etat_dossier: currentDossier.etat_dossier });

      if (error.response?.data?.error) {
        message.error(error.response.data.error);
        if (error.response.data.message) {
          Modal.warning({
            title: "Règle métier",
            content: error.response.data.message,
            width: 500,
          });
        }
      } else {
        message.error("Erreur lors de la mise à jour de l'état");
      }
    }
  };

  const handleStartModification = async (
    modificationId: number,
    dateStarted: string
  ) => {
    if (!currentDossier) return;

    try {
      await dossierApi.startModification(currentDossier.id, {
        modification_id: modificationId,
        date_started: dateStarted,
      });
      message.success("Modification commencée");
      dispatch(fetchDossierById(currentDossier.id));
    } catch (error) {
      message.error("Erreur lors du démarrage de la modification");
    }
  };

  const handleAddModification = async (
    assignmentId: number,
    observations: string
  ) => {
    if (!currentDossier) return;

    try {
      await dossierApi.addModificationRequest(currentDossier.id, {
        workflow_assignment_id: assignmentId,
        observations,
      });
      message.success("Demande de modification ajoutée");
      dispatch(fetchDossierById(currentDossier.id));
    } catch (error) {
      message.error("Erreur lors de la demande de modification");
    }
  };

  const handleCompleteModification = async (
    modificationId: number,
    dateCompleted: string,
    observations?: string
  ) => {
    if (!currentDossier) return;

    try {
      await dossierApi.completeModification(currentDossier.id, {
        modification_id: modificationId,
        date_completed: dateCompleted,
        observations,
      });
      message.success("Modification marquée comme terminée");
      dispatch(fetchDossierById(currentDossier.id));
    } catch (error) {
      message.error("Erreur lors de la completion de la modification");
    }
  };

  return (
    <FormContainer>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 24 }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate("/dossiers")}
          style={{ marginRight: 16 }}
        >
          Retour
        </Button>
        <h1>
          {mode === "create"
            ? "Nouveau dossier"
            : `Dossier ${currentDossier?.numero_dossier}`}
        </h1>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{
          priorite: 3,
        }}
      >
        {/* Informations générales */}
        <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
          <Col span={24}>
            <FormCard title="Informations générales">
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="Date_reception_demande"
                    label="Date de réception de la demande"
                    rules={[{ required: true, message: "Champ obligatoire" }]}
                  >
                    <DatePicker
                      style={{ width: "100%" }}
                      format="DD/MM/YYYY"
                      placeholder="Sélectionner une date"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="Date_envoi_creation"
                    label="Date d'envoi de création"
                  >
                    <DatePicker
                      style={{ width: "100%" }}
                      format="DD/MM/YYYY"
                      placeholder="Sélectionner une date"
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="fk_groupe_client"
                    label="Groupe client"
                    rules={[{ required: true, message: "Champ obligatoire" }]}
                  >
                    <Select
                      placeholder="Sélectionner un groupe client"
                      loading={referencesLoading}
                    >
                      {groupeClients.map((gc) => (
                        <Option key={gc.id} value={gc.id}>
                          {gc.nom}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="client"
                    label="Client"
                    rules={[{ required: true, message: "Champ obligatoire" }]}
                  >
                    <Input placeholder="Nom du client" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="conservation"
                    label="Conservation"
                    rules={[{ required: true, message: "Champ obligatoire" }]}
                  >
                    <Select placeholder="Sélectionner une conservation">
                      {conservationOptions.map((option) => (
                        <Option key={option} value={option}>
                          {option}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="fk_typologie"
                    label="Typologie"
                    rules={[{ required: true, message: "Champ obligatoire" }]}
                  >
                    <Select
                      placeholder="Sélectionner une typologie"
                      loading={referencesLoading}
                    >
                      {typologies.map((t) => (
                        <Option key={t.id} value={t.id}>
                          {t.libelle}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="fk_departement"
                    label="Département"
                    rules={[{ required: true, message: "Champ obligatoire" }]}
                  >
                    <Select
                      placeholder="Sélectionner un département"
                      loading={referencesLoading}
                    >
                      {departements.map((d) => (
                        <Option key={d.id} value={d.id}>
                          {d.nom}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="date_livraison_convenue"
                    label="Date de livraison convenue"
                  >
                    <DatePicker
                      style={{ width: "100%" }}
                      format="DD/MM/YYYY"
                      placeholder="Sélectionner une date"
                    />
                  </Form.Item>
                </Col>
              </Row>
            </FormCard>
          </Col>
        </Row>

        {/* Informations complémentaires */}
        <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
          <Col span={24}>
            <FormCard title="Informations complémentaires">
              <Row gutter={16}>
                <Col xs={24} md={6}>
                  <Form.Item name="titre_foncier" label="Titre foncier">
                    <Input placeholder="Numéro de titre foncier" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={6}>
                  <Form.Item name="indice" label="Indice">
                    <Input placeholder="Indice" />
                  </Form.Item>
                </Col>
                {/* <Col xs={24} md={6}>
                  <Form.Item name="etat_dossier" label="État du dossier">
                    <Select>
                      <Option value="en_attente_visite">
                        En attente de visite
                      </Option>
                      <Option value="en_cours_visite">
                        En cours de visite
                      </Option>
                      <Option value="en_attente_elaboration">
                        En attente d'élaboration
                      </Option>
                      <Option value="en_cours_elaboration">
                        En cours d'élaboration
                      </Option>
                      <Option value="en_attente_validation">
                        En attente de validation
                      </Option>
                      <Option value="en_cours_validation">
                        En cours de validation
                      </Option>
                      <Option value="en_attente_modification">
                        En attente de modification
                      </Option>
                      <Option value="en_cours_modification">
                        En cours de modification
                      </Option>
                      <Option value="valide_att_honoraires">
                        Validé - En attente des honoraires
                      </Option>
                      <Option value="valide_a_envoyer">
                        Validé - À envoyer
                      </Option>
                      <Option value="envoye">Envoyé</Option>
                    </Select>
                  </Form.Item>
                </Col> */}
                <Col xs={24} md={6}>
                  <Form.Item name="priorite" label="Priorité">
                    <Select>
                      <Option value={5}>CRITIQUE</Option>
                      <Option value={4}>URGENT</Option>
                      <Option value={3}>MOYEN</Option>
                      <Option value={2}>NORMAL</Option>
                      <Option value={1}>FAIBLE</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} md={6}>
                  <Form.Item name="cp" label="CP">
                    <Select placeholder="Statut CP" allowClear>
                      <Option value="DISPO">Disponible</Option>
                      <Option value="DISPO_ANCIEN">Disponible Ancien</Option>
                      <Option value="ATT_CLIENT">Attente Client</Option>
                      <Option value="ATT_EN_LIGNE">Attente En Ligne</Option>
                      <Option value="ATT_VISITEUR">Attente Visiteur</Option>
                      <Option value="ATT_TOPO">Attente Topo</Option>
                      <Option value="PROB">Problème</Option>
                      <Option value="A_DEMANDER_AU_TOPO">
                        À Demander au Topo
                      </Option>
                      <Option value="A_DEMANDER_EN_LIGNE">
                        À Demander En Ligne
                      </Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} md={6}>
                  <Form.Item name="pc" label="PC">
                    <Select placeholder="Statut PC" allowClear>
                      <Option value="DISPO">Disponible</Option>
                      <Option value="DISPO_ANCIEN">Disponible Ancien</Option>
                      <Option value="ATT_CLIENT">Attente Client</Option>
                      <Option value="ATT_EN_LIGNE">Attente En Ligne</Option>
                      <Option value="ATT_VISITEUR">Attente Visiteur</Option>
                      <Option value="ATT_TOPO">Attente Topo</Option>
                      <Option value="PROB">Problème</Option>
                      <Option value="A_DEMANDER_AU_TOPO">
                        À Demander au Topo
                      </Option>
                      <Option value="A_DEMANDER_EN_LIGNE">
                        À Demander En Ligne
                      </Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} md={6}>
                  <Form.Item name="pa" label="PA">
                    <Select placeholder="Statut PA" allowClear>
                      <Option value="DISPO">Disponible</Option>
                      <Option value="DISPO_ANCIEN">Disponible Ancien</Option>
                      <Option value="ATT_CLIENT">Attente Client</Option>
                      <Option value="ATT_EN_LIGNE">Attente En Ligne</Option>
                      <Option value="ATT_VISITEUR">Attente Visiteur</Option>
                      <Option value="ATT_TOPO">Attente Topo</Option>
                      <Option value="PROB">Problème</Option>
                      <Option value="A_DEMANDER_AU_TOPO">
                        À Demander au Topo
                      </Option>
                      <Option value="A_DEMANDER_EN_LIGNE">
                        À Demander En Ligne
                      </Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} md={6}>
                  <Form.Item name="nr_c_ch" label="NR C CH">
                    <Select placeholder="Statut NR C CH" allowClear>
                      <Option value="DISPO">Disponible</Option>
                      <Option value="DISPO_ANCIEN">Disponible Ancien</Option>
                      <Option value="ATT_CLIENT">Attente Client</Option>
                      <Option value="ATT_EN_LIGNE">Attente En Ligne</Option>
                      <Option value="ATT_VISITEUR">Attente Visiteur</Option>
                      <Option value="ATT_TOPO">Attente Topo</Option>
                      <Option value="PROB">Problème</Option>
                      <Option value="A_DEMANDER_AU_TOPO">
                        À Demander au Topo
                      </Option>
                      <Option value="A_DEMANDER_EN_LIGNE">
                        À Demander En Ligne
                      </Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            </FormCard>
          </Col>
        </Row>

        {/* Section Facturation */}
        <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
          <Col span={24}>
            <FormCard title="Facturation">
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item name="honoraires_ht" label="Honoraires HT (MAD)">
                    <InputNumber
                      style={{ width: "100%" }}
                      placeholder="0.00"
                      min={0}
                      precision={2}
                      formatter={formatNumber}
                      parser={parseNumber}
                      addonAfter="MAD HT"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="ndf_mad" label="NDF (MAD)">
                    <InputNumber
                      style={{ width: "100%" }}
                      placeholder="0.00"
                      min={0}
                      precision={2}
                      formatter={formatNumber}
                      parser={parseNumber}
                      addonAfter="MAD"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    name="honoraires_payes"
                    label="Statut paiement"
                    valuePropName="checked"
                  >
                    <Switch
                      checkedChildren="Payé"
                      unCheckedChildren="En attente"
                    />
                  </Form.Item>
                </Col>
              </Row>
            </FormCard>
          </Col>
        </Row>

        {/* Localisation */}
        <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
          <Col span={24}>
            <FormCard title="Localisation">
              <Row gutter={16}>
                <Col xs={24} md={8}>
                  <Form.Item
                    name="fk_region"
                    label="Région"
                    rules={[{ required: true, message: "Champ obligatoire" }]}
                  >
                    <Select
                      placeholder="Sélectionner une région"
                      onChange={handleRegionChange}
                      loading={referencesLoading}
                    >
                      {regions.map((r) => (
                        <Option key={r.id} value={r.id}>
                          {r.nom}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    name="fk_province"
                    label="Province"
                    rules={[{ required: true, message: "Champ obligatoire" }]}
                  >
                    <Select
                      placeholder="Sélectionner une province"
                      onChange={handleProvinceChange}
                      disabled={!selectedRegion}
                    >
                      {filteredProvinces.map((p) => (
                        <Option key={p.id} value={p.id}>
                          {p.nom}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    name="fk_commune"
                    label="Commune"
                    rules={[{ required: true, message: "Champ obligatoire" }]}
                  >
                    <Select
                      placeholder="Sélectionner une commune"
                      disabled={!selectedProvince}
                    >
                      {filteredCommunes.map((c) => (
                        <Option key={c.id} value={c.id}>
                          {c.nom}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            </FormCard>
          </Col>
        </Row>
        {mode === "edit" && currentDossier && (
                      <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
                        <Col span={24}>
                          <FormCard
                            title={
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                <SettingOutlined
                                  style={{ marginRight: 8, color: "#722ed1" }}
                                />
                                État du dossier (automatique)
                                <Tag
                                  color="green"
                                  style={{ marginLeft: 12, fontSize: "11px" }}
                                >
                                  Automatisé
                                </Tag>
                              </div>
                            }
                            style={{
                              background:
                                "linear-gradient(135deg, #f6ffed 0%, #f0f9ff 100%)",
                              border: "1px solid #b7eb8f",
                            }}
                          >
                            <div
                              style={{ textAlign: "center", padding: "24px 0" }}
                            >
                              <div style={{ marginBottom: 16 }}>
                                <Text type="secondary" style={{ fontSize: 16 }}>
                                  État actuel du dossier :
                                </Text>
                              </div>

                              <Tag
                                color={getEtatColor(
                                  currentDossier.etat_dossier
                                )}
                                style={{
                                  fontSize: 18,
                                  padding: "8px 20px",
                                  borderRadius: 20,
                                  fontWeight: 600,
                                }}
                              >
                                {getEtatText(currentDossier.etat_dossier)}
                              </Tag>

                              <div style={{ marginTop: 16 }}>
                                <Text type="secondary" style={{ fontSize: 14 }}>
                                  L'état se met à jour automatiquement selon le
                                  workflow et les assignations
                                </Text>
                              </div>
                            </div>

                            
                          </FormCard>
                        </Col>
                      </Row>
                    )}

        {/* WORKFLOW SECTIONS - Show in both create and edit modes */}
        <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
          <Col span={24}>
            <WorkflowCard title="Assignation des équipes">
              {mode === "create" ? (
                // Simple workflow assignment for CREATE mode
                <Row gutter={[16, 16]}>
                  <Col xs={24} md={8}>
                    <SimpleWorkflowSection
                      role="visiteur"
                      title="Visiteurs"
                      icon={<EyeOutlined style={{ color: "#1890ff" }} />}
                    />
                  </Col>
                  <Col xs={24} md={8}>
                    <SimpleWorkflowSection
                      role="elaborateur"
                      title="Élaborateurs"
                      icon={<EditOutlined style={{ color: "#722ed1" }} />}
                    />
                  </Col>
                  <Col xs={24} md={8}>
                    <SimpleWorkflowSection
                      role="validateur"
                      title="Validateurs"
                      icon={
                        <CheckCircleOutlined style={{ color: "#52c41a" }} />
                      }
                    />
                  </Col>
                </Row>
              ) : (
                // Full workflow management for EDIT mode
                <Row gutter={[24, 24]}>
                  {/* Visiteurs Section */}
                  <Col span={24}>
                    <WorkflowAssignments
                      assignments={visiteurs}
                      users={getUsersByRole("visiteur")}
                      role="visiteur"
                      title="Visiteurs"
                      icon={<EyeOutlined style={{ color: "#1890ff" }} />}
                      canAdd={true}
                      onAddAssignment={handleAddAssignment}
                      onUpdateAssignment={handleUpdateAssignment}
                      onRemoveAssignment={handleRemoveAssignment}
                    />
                  </Col>

                  {/* Elaborateurs Section */}
                  <Col span={24}>
                    <WorkflowAssignments
                      assignments={elaborateurs}
                      users={getUsersByRole("elaborateur")}
                      role="elaborateur"
                      title="Élaborateurs"
                      icon={<EditOutlined style={{ color: "#722ed1" }} />}
                      canAdd={true}
                      onAddAssignment={handleAddAssignment}
                      onUpdateAssignment={handleUpdateAssignment}
                      onRemoveAssignment={handleRemoveAssignment}
                      onAddModification={handleAddModification}
                      onStartModification={handleStartModification} // NOUVEAU
                      onCompleteModification={handleCompleteModification}
                    />
                  </Col>

                  {/* Validateurs Section */}
                  <Col span={24}>
                    <WorkflowAssignments
                      assignments={validateurs}
                      users={getUsersByRole("validateur")}
                      role="validateur"
                      title="Validateurs"
                      icon={
                        <CheckCircleOutlined style={{ color: "#52c41a" }} />
                      }
                      canAdd={true}
                      canAssignValidators={canAssignValidators()}
                      onAddAssignment={handleAddAssignment}
                      onUpdateAssignment={handleUpdateAssignment}
                      onRemoveAssignment={handleRemoveAssignment}
                    />
                  </Col>
                </Row>
              )}
            </WorkflowCard>
          </Col>
        </Row>

        {/* Final Completion Section - Only in edit mode */}
        {mode === "edit" && (
          <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
            <Col span={24}>
              <FormCard title="Finalisation du dossier">
                <Row gutter={16}>
                  <Col xs={24} md={6}>
                    <Form.Item
                      name="date_envoi_rapport"
                      label="Date d'envoi du rapport"
                    >
                      <DatePicker
                        style={{ width: "100%" }}
                        format="DD/MM/YYYY"
                        placeholder="Sélectionner une date"
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </FormCard>
            </Col>
          </Row>
        )}

        <FormActions>
          <Button onClick={() => navigate("/dossiers")} size="large">
            Annuler
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            icon={<SaveOutlined />}
            loading={saving}
            size="large"
          >
            {mode === "create" ? "Créer le dossier" : "Modifier le dossier"}
          </Button>
        </FormActions>
      </Form>
    </FormContainer>
  );
};

export default DossierForm;
